"use client";

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutContext } from '../layout-context-container';

function StickyHeader({fixedTopRef = null, innerHeaderRef = null, children, isHCP = false}) {
  const {
    cookieStored,
    setStickyHeaderFixedState,
    showMobileMenu,
    screenshotMode,
    indicationsMobileStateCollapsed,
    isMobile,
    showCookieConsent,
    autoScrollActive,
  } = useContext(LayoutContext);
  const stickyHeaderRef = useRef(null);
  const wrapperRef = useRef(null);
  const [menuFixed, _setMenuFixed] = useState(false);
  const _menuFixed = useRef(menuFixed);
  const setMenuFixed = (/** @type {boolean} */ val) => {
    _setMenuFixed(val);
    _menuFixed.current = val;
  }
  const [positionFixedReady, _setPositionFixedReady] = useState(false);
  const _positionFixedReady = useRef(positionFixedReady); 
  const setPositionFixedReady = (/** @type {boolean} */ val) => {
    _setPositionFixedReady(val);
    _positionFixedReady.current = val;
  }
  const [lastHeight, _setLastHeight] = useState(0);
  const _lastHeight = useRef(lastHeight);
  const setLastHeight = (/** @type {Number} */ val) => {
    _setLastHeight(val);
    _lastHeight.current = val;
  }

  useEffect(() => {
    // handle sticky header show/hide logic
    let currentYOffset = 0;
    let travel = 0;

    //// adjust saved offset if the subnav is opened to prevent the menu from going away ///////
    const headerRef = stickyHeaderRef.current;
    function handleNavClick(){
      const hasSubNavSpacing = stickyHeaderRef.current?.classList.contains("subnav-spacing");
      if (!hasSubNavSpacing){
        currentYOffset += 59;
      }
    }

    headerRef?.addEventListener('click', handleNavClick);


    function handleScroll() {
      const offsetY = window.scrollY;
      
      const fixedTopHeight = fixedTopRef?.current?.getBoundingClientRect().height || 0;
      const innerHeaderHeight = innerHeaderRef?.current?.getBoundingClientRect().height || 0;
      const headerHeight = wrapperRef.current?.getBoundingClientRect().height;

      if ((headerHeight > 0 && showMobileMenu) || (headerHeight === 0 && showCookieConsent === false && showMobileMenu)) {
        if (window.innerWidth < 700) {
          setLastHeight(innerHeaderHeight + fixedTopHeight);
        } else {
          setLastHeight(headerHeight);
        }
      }

      if (currentYOffset >= offsetY && offsetY >= headerHeight && !_menuFixed.current && !autoScrollActive) {
        if (!_menuFixed.current && travel >= 10) {
          // show header after scrolling 10 pixels
          setMenuFixed(true);
        } else {
          travel++;
        }
      } else if (currentYOffset < offsetY && _menuFixed.current) {
        setMenuFixed(false);
        travel = 0;
      }

      if (offsetY <= 0) {
        setMenuFixed(false);
        travel = 0;
      }

      if (offsetY >= headerHeight && !_positionFixedReady.current && travel >= 10) {
        setPositionFixedReady(true);
      } else if (offsetY < headerHeight) {
        setPositionFixedReady(false);
      }

      currentYOffset = offsetY;
    }
    
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll',handleScroll);
      headerRef?.removeEventListener('click', handleNavClick);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScrollActive]);

  function handleResize() {
    // reset height
    const fixedTopHeight = fixedTopRef?.current?.getBoundingClientRect().height || 0;
    const innerHeaderHeight = innerHeaderRef?.current?.getBoundingClientRect().height || 0;
    const headerHeight = wrapperRef.current?.getBoundingClientRect().height;

    if (headerHeight > 0 || (headerHeight === 0 && showCookieConsent === false)) {
      if (window.innerWidth < 700) {
        setLastHeight(innerHeaderHeight + fixedTopHeight);
      } else {
        setLastHeight(headerHeight);
      }
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleResize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicationsMobileStateCollapsed, isMobile]);


  useEffect(() => {
    // init header height when rendered
    if (wrapperRef.current !== undefined || cookieStored) {
      handleResize();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, cookieStored]);

  useEffect(() => {
    if (!showMobileMenu) {
      handleResize();
    } else {
      wrapperRef.current?.scrollTo({top: 0});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMobileMenu]);

  useEffect(() => {
    // on mobile size the height needs a small adjustment
    function onResize() {
      let navHeight = 0;

      if (window.innerWidth < 699) {
        navHeight = 113;

        const banner = document.querySelector('.home-conference-banner:not(.home-conference-banner--hidden)');
        if (banner) {
          navHeight += banner.clientHeight;
        }
      }

      // set header height to create space for position fixed
      if (lastHeight > 0 || (lastHeight === 0 && showCookieConsent === false)) {
        // @ts-ignore
        stickyHeaderRef.current?.style.setProperty('height', `${_lastHeight.current + navHeight}px`);
        // @ts-ignore
        stickyHeaderRef.current?.style.setProperty('--sticky-header-top', `-${_lastHeight.current + navHeight}px`);
      }
    }

    if (isHCP) {
      window.addEventListener('resize', onResize);
      onResize();
      //setStickyHeaderFixedState(menuFixed);
    } else {
      // set header height to create space for position fixed
      if (lastHeight > 0 || (lastHeight === 0 && showCookieConsent === false)) {
        // @ts-ignore
        stickyHeaderRef.current?.style.setProperty('height', `${lastHeight}px`);
        // @ts-ignore
        stickyHeaderRef.current?.style.setProperty('--sticky-header-top', `-${lastHeight}px`);
      }

      setStickyHeaderFixedState(menuFixed);
    }
    return () => {
      if (isHCP) {
        window.removeEventListener('resize', onResize);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuFixed, lastHeight, stickyHeaderRef, wrapperRef, cookieStored]);

  const containerClass = useMemo(() => {
    var className = 'sticky-header';

    if (menuFixed && !screenshotMode) {
      className = `${className} sticky-header--fixed`;
    }

    if (positionFixedReady && !screenshotMode) {
      className = `${className} sticky-header--ready`;
    }

    if (isHCP) {
      className = `${className} sticky-header--hcp`;
    }

    return className;
  }, [menuFixed, positionFixedReady, screenshotMode, isHCP]);

  return (
    <div className={containerClass} ref={stickyHeaderRef}>
      <div className="sticky-header__wrapper" ref={wrapperRef}>
        {children}
      </div>
    </div>
  );
}

export default StickyHeader;